import { MainHeader, SectionParagraph } from '@/components/shared/Content';
import { type UseContentProps, useContent } from '@/hooks/useContent';
import { font } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { useAnalyticsContext, useComplianceProvider } from '@lemonade-hq/nuiza';

import React, { useCallback, useEffect } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';

// Common styles
const commonTextStyles = css`
  text-align: left;
  margin-bottom: 20px;
`;

const commonFontStyles = css`
  ${font('main', { fontSize: '1.8rem' })}
`;

const commonListStyles = css`
  ${commonFontStyles}
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;

  li {
    line-height: 2.8rem;
  }
`;

const commonTableStyles = css`
  ${commonFontStyles}
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  table-layout: fixed;
  display: table;
`;

// Global styles
const GlobalStyle = createGlobalStyle`
  em {
    font-style: italic;
  }

  * {
    scroll-behavior: smooth;
  }

  #we, #table {
    margin-top: -80px;
    padding-top: 80px;
  }

  .big-p p {
    ${font('main', { fontSize: '3rem', lineHeight: '38px' })}
  }
`;

// Heading components
const Heading1 = styled(MainHeader)`
  margin-bottom: 30px;
  margin-top: 70px;
  ${MEDIA_QUERIES.mobile`
    word-wrap: break-word;
  `}
`;

const Heading2 = styled.h2`
  ${font('main', { fontSize: '3.2rem', fontWeight: 700 })}
  ${commonTextStyles}
  margin: 30px 0 10px 0;
`;

const Heading3 = styled.h3`
  ${font('main', { fontSize: '2.1rem', fontWeight: 700 })}
  margin-bottom: 20px;
`;

const Heading4 = styled.h4`
  ${font('main', { fontSize: '1.8rem', fontWeight: 700 })}
  text-align: left;
`;

const PageSubtitle = styled(Heading4)`
  text-align: center;
  margin-bottom: 40px;
`;

// Text component
const Text = styled(SectionParagraph)`
  ${commonTextStyles}
  width: 100%;
  white-space: normal;
  text-wrap: pretty;

  strong {
    font-weight: 700;
  }
`;

// List components
const BulletList = styled.ul`
  ${commonListStyles}
  list-style-type: none;
  margin-left: 20px;

  li::before {
    font-size: 2.2rem;
    content: '•';
    display: inline-block;
    width: 1em;
    margin-left: -1em;
  }

  li ul li:before {
    content: '◦';
  }
`;

const OrderedList = styled.ol`
  ${commonListStyles}
  list-style-type: decimal;
  margin-left: 18px;

  ol {
    list-style-type: lower-alpha;
  }
`;

// Table components
const StyledTableDesktop = styled.table`
  ${commonTableStyles}

  ${MEDIA_QUERIES.mobile`
    display: none;
  `}

  thead {
    background-color: #f8f8f8;

    tr th {
      font-weight: 700;
      font-size: 1.2rem;
      text-transform: uppercase;
    }
  }

  th,
  td {
    border: solid 1px #e0e0e0;
    padding: 10px;
    text-align: left;
    vertical-align: middle;
    height: 60px;
  }

  tr {
    border-bottom: 1px solid #e0e0e0;
  }

  td {
    font-size: 1.6rem;
    position: relative;
    padding: 20px;
  }
`;

const StyledTableMobile = styled.table`
  ${commonTableStyles}
  border: solid 1px #e0e0e0;

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none;
  `}

  tr {
    border: solid 1px #e0e0e0;
  }

  td {
    font-size: 1.6rem;
    padding: 20px;
    position: relative;
  }
`;

const TableCheck = styled.span`
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%);
  font-size: 1.8rem;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
`;

// Types
type TableItem = {
  text?: string;
  check?: 'true' | 'false';
  textMobileChecked?: string;
  textMobileUnChecked?: string;
};

type TableRow = {
  heading?: boolean;
  items: TableItem[];
};

interface TableProps {
  rows: TableRow[];
}

// Table components
const THead: React.FC<TableProps> = ({ rows }) => (
  <thead>
    {rows.map((row, rowIndex) => (
      <tr key={`header-row-${rowIndex}`}>
        {row.items.map((item, itemIndex) => (
          <th key={`header-item-${itemIndex}`}>{item.text}</th>
        ))}
      </tr>
    ))}
  </thead>
);

const TBody: React.FC<TableProps> = ({ rows }) => (
  <tbody>
    {rows.map((row, rowIndex) => (
      <tr key={`body-row-${rowIndex}`}>
        {row.items.map((item, itemIndex) => (
          <td
            key={`body-item-${itemIndex}`}
            style={{
              paddingLeft: item.check != null ? '45px' : '20px',
            }}
          >
            {item.check != null && <TableCheck>{item.check ? '✓' : '✗'}</TableCheck>}
            {item.text}
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

const TBodyMobile: React.FC<{ headerRows: TableRow[]; bodyRows: TableRow[] }> = ({ headerRows, bodyRows }) => {
  const headerRowsItems = headerRows[0]?.items ?? [];

  return (
    <tbody>
      {bodyRows.map((row, rowIndex) => (
        <React.Fragment key={`body-row-${rowIndex}`}>
          {row.items.map((item, itemIndex) => {
            if (itemIndex === 0) {
              return (
                <tr
                  key={`body-header-${rowIndex}`}
                  style={{
                    background: '#f8f8f8',
                    fontWeight: 700,
                    fontSize: '1.2rem',
                    textTransform: 'uppercase',
                  }}
                >
                  <td
                    key={`body-item-${itemIndex}`}
                    width="100%"
                    colSpan={2}
                    style={{
                      padding: '20px',
                      fontSize: '1.2rem',
                    }}
                  >
                    {item.check != null && <TableCheck>{item.check ? '✓' : '✗'}</TableCheck>}
                    {item.text}
                  </td>
                </tr>
              );
            }

            return (
              <tr key={`body-row-${rowIndex}-${itemIndex}`}>
                <td
                  style={{
                    paddingLeft: item.check != null ? '45px' : '20px',
                    fontSize: '1.4rem',
                  }}
                >
                  {item.check != null && <TableCheck>{item.check ? '✓' : '✗'}</TableCheck>}
                  {(item.check
                    ? headerRowsItems[itemIndex]?.textMobileChecked
                    : headerRowsItems[itemIndex]?.textMobileUnChecked) ?? headerRowsItems[itemIndex]?.text}
                </td>
                <td
                  style={{
                    fontSize: '1.4rem',
                  }}
                >
                  {item.text}
                </td>
              </tr>
            );
          })}
        </React.Fragment>
      ))}
    </tbody>
  );
};

const Table: React.FC<TableProps> = ({ rows }) => {
  const headerRows = rows.filter(row => row.heading);
  const bodyRows = rows.filter(row => !row.heading);

  return (
    <>
      <StyledTableDesktop>
        {headerRows.length > 0 && <THead rows={headerRows} />}
        <TBody rows={bodyRows} />
      </StyledTableDesktop>
      <StyledTableMobile>
        <TBodyMobile headerRows={headerRows} bodyRows={bodyRows} />
      </StyledTableMobile>
    </>
  );
};

const OpenCookieBanner: React.FC<React.PropsWithChildren<{ isSettings: boolean }>> = ({ children, isSettings }) => {
  const { canManageCookies } = useAnalyticsContext();
  const { setShowConsentBanner, setIsPersonalInfoView } = useComplianceProvider();

  const onManageCookiesClick = useCallback(() => {
    setIsPersonalInfoView(false);
    setShowConsentBanner(true);
  }, [setShowConsentBanner, setIsPersonalInfoView, canManageCookies]);

  const onManageDNS = useCallback(() => {
    setIsPersonalInfoView(true);
    setShowConsentBanner(true);
  }, [setIsPersonalInfoView, setShowConsentBanner, canManageCookies]);

  return (
    <a style={{ cursor: 'pointer' }} onClick={isSettings ? onManageDNS : onManageCookiesClick}>
      {children}
    </a>
  );
};

// Component mapping
const components = {
  h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Heading1 {...props} />,
  h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Heading2 {...props} />,
  h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Heading3 {...props} />,
  h4: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Heading4 {...props} />,
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => <Text {...props} />,
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => <BulletList {...props} />,
  ol: (props: React.HTMLAttributes<HTMLUListElement>) => <OrderedList {...props} />,
  PageSubtitle,
  Table,
  OpenCookieBanner,
};

// Main component
export const PrivacyPolicyContent: React.FC<UseContentProps> = ({ content }) => {
  const Component = useContent({ content });

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash.includes('#')) {
        window.location.hash = '';
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', handleHashChange);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('hashchange', handleHashChange);
      }
    };
  }, []);

  return (
    <>
      <GlobalStyle />
      <Component components={components} />
    </>
  );
};
